// Third-party
import { useState, useEffect } from 'react';
import { Tooltip } from 'react-tooltip';
import * as R from 'ramda';

// Components
import TableViewGenericProps from '@/components/table-view-generic-props.component';
import Table from '@/styles/table.styles';
import PopupGeneric from '@/components/popup-generic.component';
import UserMiniCard from '../components/user-mini-card.component';
import TableActions from '@/components/table-actions.component';
import { ThDefault, ColGroups } from '@/components/table.component';
import TitleAndText from '@/components/title-and-text.component';

// Views
import DevicesEditView from '@/views/devices-edit.view';

// Utils
import { getColumnFromHeaderGroupByHeaderName } from '@/libs/tables.lib';

// Styles
import { StyledContent, StyledSpan } from '@/styles/shared.styles';

// State
import { UserAuthContext } from '@/contexts/user-auth.context';
import { useContext } from 'react';	

export default function DevicesTableView() {
	const { userAuthState } = useContext(UserAuthContext);

	const [showEditPopup, setShowEditPopup] = useState(false);
	const [selectedRowIdToEdit, setSelectedRowId] = useState(null);
	const [tableViewGenericPropsRandomKey, setTableViewGenericPropsRandomKey] = useState(Math.random());

	const editRow = (rowId) => {
		setSelectedRowId(rowId);
		setShowEditPopup(true);
	};

	return (
		<StyledContent>
			<Tooltip
				id='user-not-active-tooltip'
				place='right'
				effect='solid'
			/>
			<TableViewGenericProps
				menuName='devices'
				key={tableViewGenericPropsRandomKey}
				preFilteringFunction={(data) => {
					if (userAuthState.user.permissions.devices.read === true) {
						return data;
					}

					if (R.prop('owner', userAuthState.user.permissions.devices.read)) {
						return data.filter((row) => {
							return row.idUser == userAuthState.user.id;
						});
					}

					if (
						userAuthState.user.permissions.devices.read.roles.length > 0 &&
						userAuthState.user.permissions.devices.read.roles[0] === '*'
					) {
						return data;
					}

					const filteredData = data.filter((row) => {
						return userAuthState.user.permissions.devices.read.roles.includes(row.role);
					});

					return filteredData;
				}}
				onPopupSuccessClose={() => {
					setShowEditPopup(false);
					setTableViewGenericPropsRandomKey(Math.random());
				}}>
				{({
					getTableProps,
					headerGroups,
					getTableBodyProps,
					rows,
					prepareRow,
					deleteRow,
					setPopupErrorMessage,
					setShowErrorPopup,
					setPopupSuccessMessage,
					setShowSuccessPopup,
				}) => {
					const headerGroup = headerGroups[0];
					const columnUsername = getColumnFromHeaderGroupByHeaderName(headerGroup, 'User');
					const columnLastUse = getColumnFromHeaderGroupByHeaderName(headerGroup, 'Last Use');
					return (
						<>
							{showEditPopup && (
								<PopupGeneric onClickOutside={() => {}}>
									<DevicesEditView
										requestType={'PATCH'}
										currentlySelectedId={selectedRowIdToEdit}
										onClose={() => {
											setShowEditPopup(false);
										}}
										onSubmitSuccess={() => {
											setPopupSuccessMessage('Device successfully edited.');
											setShowSuccessPopup(true);
										}}
										onSubmitError={(errorMessage) => {
											setPopupErrorMessage(errorMessage);
											setShowErrorPopup(true);
										}}
									/>
								</PopupGeneric>
							)}
							<Table {...getTableProps()}>
								<ColGroups widths={['25%', '25%', '25%', '20%', '5%']} />
								<Table.THead>
									<Table.Tr {...headerGroup.getHeaderGroupProps()}>
										<ThDefault
											column={columnUsername}
											text={columnUsername.render('Header')}
										/>
										<Table.Th>Device</Table.Th>
										<Table.Th>Description</Table.Th>
										<ThDefault
											column={columnLastUse}
											text={columnLastUse.render('Header')}
										/>
										<Table.Th></Table.Th>
									</Table.Tr>
								</Table.THead>
								<Table.TBody {...getTableBodyProps()}>
									{rows.map((row) => {
										prepareRow(row);

										const date = new Date(row.values.lastUse);
										const options = {
											year: 'numeric',
											month: '2-digit',
											day: '2-digit',
											hour: '2-digit',
											minute: '2-digit',
											second: '2-digit',
											timeZoneName: 'short',
										};
										const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);
										return (
											<Table.Tr
												{...row.getRowProps()}
												active={row.values.active == 1}>
												<Table.Td>
													<UserMiniCard
														username={row.values.username}
														email={row.values.email}
														active={row.values.active == 1}
														avatar={row.values.avatar}
													/>
												</Table.Td>
												<Table.Td>
													<TitleAndText
														title={`UID: ${row.values.deviceUniqueIdentifier}`}
														text={`OS: ${row.values.OS == '' ? 'N/A' : row.values.OS}`}
														titleFontSize='0.8rem'
													/>
												</Table.Td>
												<Table.Td>
													<StyledSpan
														style={{
															fontSize: '0.8rem',
														}}>
														{row.values.deviceDescription}
													</StyledSpan>
												</Table.Td>
												<Table.Td>
													<StyledSpan
														style={{
															fontSize: '0.8rem',
														}}>
														{formattedDate}
													</StyledSpan>
												</Table.Td>
												<Table.Td>
													{userAuthState.user.permissions.devices && userAuthState.user.permissions.devices.update && (
														<TableActions
															onEditRow={() => {
																editRow(row.values.idDevice);
															}}
															onDeleteRow={() => {
																deleteRow(row.values.idDevice);
															}}
														/>
													)}
												</Table.Td>
											</Table.Tr>
										);
									})}
								</Table.TBody>
							</Table>
						</>
					);
				}}
			</TableViewGenericProps>
		</StyledContent>
	);
}
